import { useCallback, useEffect, useRef, useState } from "react";

import type { UseAdminReadOptions, UseAdminReadResult } from "./useAdminRead.types";

export function useAdminRead<T>({ load, loadOnMount = true }: UseAdminReadOptions<T>): UseAdminReadResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(loadOnMount);
  const sequence = useRef(0);

  const refresh = useCallback(async (): Promise<T | null> => {
    const current = ++sequence.current;
    setIsLoading(true);
    setError(null);
    try {
      const value = await load();
      if (current === sequence.current) setData(value);
      return value;
    } catch (reason) {
      if (current === sequence.current) {
        setError(reason instanceof Error ? reason.message : "Yönetim verisi yüklenemedi.");
      }
      return null;
    } finally {
      if (current === sequence.current) setIsLoading(false);
    }
  }, [load]);

  useEffect(() => {
    if (loadOnMount) void refresh();
    return () => { sequence.current += 1; };
  }, [loadOnMount, refresh]);

  const reset = useCallback(() => {
    sequence.current += 1;
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, error, isLoading, refresh, reset };
}
